import React, { useEffect, useMemo, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Card, CardContent, Chip, Grid, LinearProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, Typography } from '@mui/material';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EnrollmentHistory from './EnrollmentHistory';
import EnrollmentCohortReport from './EnrollmentCohortReport';
import { getAllClassrooms } from '../../api/endpoints/sponte';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getAllMatriculas } from '../../api/sponteAPI';
import { aulas_inicio_censo_escolar } from '../../utils/YEARCONSTS';
import { buildEnrollmentStatistics, readEnrollmentResponse, textValue, gradeSort } from './enrollmentStatistics';
const EMPTY = [];
const COLORS = ['#1677cf', '#11a797', '#9b59b6', '#ef9b24', '#64748b'];
const panel = {
  p: {
    xs: 2,
    md: 3
  },
  borderRadius: 3,
  border: '1px solid #e1e9f0',
  boxShadow: '0 4px 18px #173b5710'
};
const format = value => value === null ? '—' : value.toLocaleString('pt-BR');
const getUnitKey = () => {
  try {
    const unit = JSON.parse(sessionStorage.getItem('loggedUser') || '{}').unit;
    return unit === 'CAMPINA GRANDE' ? 'campina' : unit === 'PATOS' ? 'patos' : null;
  } catch {
    return null;
  }
};
const apiDate = (year, unit) => {
  const value = aulas_inicio_censo_escolar.anoLetivo[year]?.[unit];
  if (!value) throw new Error('Calendário não configurado');
  return value.split('/').reverse().join('/');
};
export function DistributionCard({
  title,
  rows,
  total
}) {
  return <Paper sx={{
    ...panel,
    height: '100%',
    boxSizing: 'border-box'
  }}>
    <Typography variant="h6" sx={{
      fontSize: 18,
      fontWeight: 700
    }}>{title}</Typography>
    <Box sx={{
      height: 200,
      minWidth: 0
    }} aria-hidden="true">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart><Pie data={rows} dataKey="value" nameKey="name" innerRadius="52%" outerRadius="82%" paddingAngle={2} isAnimationActive={false} stroke="none">
          {rows.map((row, index) => <Cell key={row.name} fill={COLORS[index % COLORS.length]} />)}
        </Pie><Tooltip formatter={value => [format(value), 'Matrículas']} /></PieChart>
      </ResponsiveContainer>
    </Box>
    <Box component="ul" sx={{
      m: 0,
      p: 0,
      listStyle: 'none'
    }}>
      {rows.map((row, index) => <Box component="li" key={row.name} sx={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 1,
        py: .75,
        borderBottom: '1px solid #edf2f7',
        fontSize: 14
      }}>
        <Box component="span" sx={{
          width: 9,
          height: 9,
          flexShrink: 0,
          borderRadius: '50%',
          bgcolor: COLORS[index % COLORS.length]
        }} />
        <Box component="span" sx={{
          flex: 1,
          minWidth: 0,
          overflowWrap: 'anywhere'
        }}>{row.name}</Box>
        <Box component="strong">{format(row.value)}</Box>
        <Box component="span" sx={{
          color: 'text.secondary',
          minWidth: 43,
          textAlign: 'right'
        }}>{total ? (row.value / total * 100).toFixed(1).replace('.', ',') : '0'}%</Box>
      </Box>)}
      {!rows.length && <Typography color="text.secondary">Sem matrículas nesta base.</Typography>}
    </Box>
  </Paper>;
}
function StatisticsTable({
  title,
  description,
  columns,
  rows,
  sort,
  onSort
}) {
  return <Paper sx={{
    ...panel,
    minWidth: 0
  }}>
    <Typography variant="h6" sx={{
      fontWeight: 700
    }}>{title}</Typography>
    <Typography variant="body2" color="text.secondary" sx={{
      mt: .5,
      mb: 2
    }}>{description}</Typography>
    <TableContainer sx={{
      overflowX: 'auto',
      border: '1px solid #e6edf3',
      borderRadius: 2
    }}>
      <Table size="small" aria-label={title} sx={{
        minWidth: 720,
        '& th': {
          bgcolor: '#eff5fa',
          color: '#244760',
          fontWeight: 700,
          py: 1.5
        },
        '& td': {
          py: 1.4
        },
        '& tbody tr:nth-of-type(even)': {
          bgcolor: '#f8fafc'
        },
        '& tbody tr:hover': {
          bgcolor: '#edf6fd'
        },
        '& td, & th': {
          fontFamily: 'inherit'
        }
      }}>
        <TableHead><TableRow>{columns.map(column => <TableCell key={column.key} align={column.key === 'name' ? 'left' : 'center'} sortDirection={sort?.key === column.key ? sort.dir : false}>
          {onSort ? <TableSortLabel active={sort.key === column.key} direction={sort.key === column.key ? sort.dir : 'asc'} onClick={() => onSort(column.key)}>{column.label}</TableSortLabel> : column.label}
        </TableCell>)}</TableRow></TableHead>
        <TableBody>{rows.map(row => <TableRow key={row.key || row.name}>{columns.map(column => <TableCell key={column.key} align={column.key === 'name' ? 'left' : 'center'} sx={{
              fontWeight: column.key === 'total' ? 700 : 400,
              color: column.key === 'total' ? '#1677cf' : undefined
            }}>
          {typeof row[column.key] === 'number' || row[column.key] === null ? format(row[column.key]) : row[column.key]}
        </TableCell>)}</TableRow>)}</TableBody>
      </Table>
    </TableContainer>
    <Typography variant="caption" color="text.secondary" sx={{
      display: {
        xs: 'block',
        md: 'none'
      },
      mt: 1
    }}>Deslize a tabela para ver todas as colunas.</Typography>
  </Paper>;
}
export const AnaliseEstatistica = ({
  data = {}
}) => {
  const matriculas = data.matriculas || EMPTY;
  const year = Number(textValue(data.anoLetivo || matriculas[0]?.AnoLetivo));
  const unit = getUnitKey();
  const scope = `${unit}:${year}`;
  const situation = {
    1: 'Vigentes',
    2: 'Encerrados',
    3: 'Rescindidos',
    6: 'Pré-matrículas'
  }[Number(data.situacaoID)];
  const [reload, setReload] = useState(0);
  const [remote, setRemote] = useState({
    scope: '',
    previous: null,
    rescinded: null,
    loading: true,
    errors: [],
    catalogs: {}
  });
  const [sort, setSort] = useState({
    key: 'name',
    dir: 'asc'
  });
  const previous = remote.scope === scope ? remote.previous : null;
  const rescinded = remote.scope === scope ? remote.rescinded : null;
  const loading = remote.scope !== scope || remote.loading;
  useEffect(() => {
    let cancelled = false;
    setRemote({
      scope,
      previous: null,
      rescinded: null,
      loading: true,
      errors: [],
      catalogs: {}
    });
    const query = async (targetYear, status) => {
      let timeout;
      try {
        const payload = await Promise.race([getAllMatriculas(apiDate(targetYear, unit), status), new Promise((_, reject) => {
          timeout = setTimeout(() => reject(new Error('Tempo de consulta excedido')), 60000);
        })]);
        return readEnrollmentResponse(payload);
      } finally {
        clearTimeout(timeout);
      }
    };
    // Três bases de contratos e dois cadastros oficiais de turma. Falhas históricas não viram zero, e respostas antigas são descartadas.
    const load = async () => {
      if ((!matriculas.length && !data.queryConfirmed) || !Number.isInteger(year) || !unit) {
        if (!cancelled) setRemote({
          scope,
          loading: false,
          previous: null,
          rescinded: null,
          errors: matriculas.length ? ['Ano letivo ou unidade não identificado. Refaça a consulta do relatório.'] : []
        });
        return;
      }
      const catalog = async targetYear => {
        let timer;
        try {
          const value = await Promise.race([getAllClassrooms(targetYear), new Promise((_, reject) => {
            timer = setTimeout(() => reject(new Error('Tempo de consulta excedido')), 60000);
          })]);
          if (!Array.isArray(value)) throw new Error('Cadastro não confirmado');
          for (const row of value) {
            const code = textValue(row.RetornoOperacao);
            if (code && !/^(?:0?1|43)(?:\s*-|\s*$)/.test(code)) throw new Error('Cadastro não confirmado');
          }
          return value;
        } finally {
          clearTimeout(timer);
        }
      };
      const results = await Promise.allSettled([query(year - 1, 1), query(year - 1, 2), query(year, 3), catalog(year), catalog(year - 1)]);
      if (cancelled) return;
      const priorOk = results[0].status === 'fulfilled' && results[1].status === 'fulfilled';
      const endedOk = results[2].status === 'fulfilled';
      setRemote({
        scope,
        loading: false,
        previous: priorOk ? [...results[0].value, ...results[1].value] : null,
        rescinded: endedOk ? results[2].value : null,
        catalogs: {
          current: results[3].status === 'fulfilled' ? results[3].value : null,
          previous: results[4].status === 'fulfilled' ? results[4].value : null
        },
        errors: [...(!priorOk ? ['Não foi possível confirmar a base do ano anterior.'] : []), ...(!endedOk ? ['Não foi possível confirmar os contratos rescindidos.'] : []), ...([results[3], results[4]].some(result => result.status === 'rejected') ? ['Cadastro oficial de turmas indisponível; nomes e turnos usam os contratos recebidos.'] : [])]
      });
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [scope, year, unit, reload, matriculas, data.queryConfirmed]);
  const catalogs = remote.scope === scope ? remote.catalogs : undefined;
  const stats = useMemo(() => buildEnrollmentStatistics(matriculas, previous, rescinded, year, catalogs), [matriculas, previous, rescinded, year, catalogs]);
  const classrooms = useMemo(() => [...stats.classrooms].sort((a, b) => {
    const left = a[sort.key],
      right = b[sort.key];
    const result = typeof left === 'number' && typeof right === 'number' ? left - right : String(left ?? '').localeCompare(String(right ?? ''), 'pt-BR', {
      numeric: true
    });
    return sort.dir === 'asc' ? result : -result;
  }), [stats, sort]);
  const columns = [{
    key: 'name',
    label: 'Turma'
  }, {
    key: 'level',
    label: 'Segmento'
  }, {
    key: 'shift',
    label: 'Turno'
  }, {
    key: 'new',
    label: 'Novatos'
  }, {
    key: 'returning',
    label: 'Veteranos'
  }, ...(stats.unknown ? [{
    key: 'unknown',
    label: 'Tipo não informado'
  }] : []), {
    key: 'total',
    label: String(year)
  }, {
    key: 'previous',
    label: String(year - 1)
  }, {
    key: 'rescinded',
    label: 'Rescindidos'
  }];
  if (!matriculas.length && !data.queryConfirmed) return <Alert severity="info">Execute a busca no Relatório de Matrículas e abra a análise para conferir os dados.</Alert>;
  if (!stats.audit.valid) return <Alert severity="error">Os totais da análise não fecharam. Refaça a consulta antes de usar estes números.</Alert>;
  return <Box sx={{
    p: {
      xs: 1,
      md: 3
    },
    fontFamily: 'Roboto, Arial, sans-serif',
    color: '#193c55'
  }}>
    <Box sx={{
      display: 'flex',
      gap: 2,
      flexWrap: 'wrap',
      alignItems: 'center',
      justifyContent: 'space-between',
      mb: 3
    }}>
      <Box><Typography variant="h4" sx={{
          fontSize: {
            xs: 25,
            md: 30
          },
          fontWeight: 700
        }}>Análise estatística · {year}</Typography>
        <Typography color="text.secondary" variant="body2" sx={{
          mt: 1
        }}>Matrículas da consulta do relatório{situation ? ` · ${situation}` : ''}, sem turmas de integral.</Typography>
      </Box>
      <Button startIcon={<RefreshIcon />} onClick={() => setReload(value => value + 1)} disabled={loading}>Atualizar comparação</Button>
    </Box>
    {loading && <Box sx={{
      mb: 2
    }}><LinearProgress /><Typography variant="caption">Consultando ano anterior e contratos rescindidos…</Typography></Box>}
    {remote.scope === scope && remote.errors.length > 0 && <Alert severity="warning" sx={{
      mb: 2
    }}>{remote.errors.join(' ')} Os valores indisponíveis aparecem como —. Tente atualizar a comparação.</Alert>}
    {(stats.audit.multipleCurrentGrades > 0 || stats.audit.multiplePreviousGrades > 0 || stats.audit.contradictoryNew > 0 || stats.audit.multipleContracts > 0) && <Alert severity="warning" sx={{
      mb: 2
    }}>Conferência do cadastro: {stats.audit.multipleContracts} contrato(s) além da contagem de alunos distintos; {stats.audit.multipleCurrentGrades} aluno(s) com mais de uma série atual; {stats.audit.multiplePreviousGrades} com mais de uma série de origem; {stats.audit.contradictoryNew} da base anterior marcado(s) como novato(s). O tipo contratual foi preservado; confira os destinos na rematrícula.</Alert>}
    <Grid container spacing={2} sx={{
      mb: 3
    }}>
      {[['Matrículas', stats.total, `${format(stats.students)} alunos distintos`], ['Veteranos', stats.returning, 'Tipo contratual: Rematrícula'], ['Novatos', stats.new, 'Tipo contratual: Matrícula'], ['Contratos rescindidos', stats.rescinded, 'Situação 3 na Sponte']].map(([label, value, detail], index) => <Grid item xs={12} sm={6} md={3} key={label}>
        <Card sx={{
          borderRadius: 3,
          height: '100%',
          border: '1px solid #e1e9f0',
          boxShadow: 'none',
          borderTop: `4px solid ${COLORS[index]}`
        }}><CardContent>
          <Typography variant="body2" color="text.secondary">{label}</Typography><Typography sx={{
              fontSize: 34,
              fontWeight: 700,
              my: .5
            }}>{format(value)}</Typography><Typography variant="caption" color="text.secondary">{detail}</Typography>
        </CardContent></Card>
      </Grid>)}
    </Grid>
    {stats.unknown > 0 && <Alert severity="info" sx={{
      mb: 3
    }}>{stats.unknown} matrícula(s) sem tipo identificado estão incluídas no total e na distribuição.</Alert>}
    <Grid container spacing={2} sx={{
      mb: 3
    }}>
      {[['Distribuição por segmento', stats.segments], ['Distribuição por turno', stats.shifts], ['Novatos e veteranos', stats.types]].map(([title, rows]) => <Grid item xs={12} md={4} key={title}><DistributionCard title={title} rows={rows} total={stats.total} /></Grid>)}
    </Grid>
    <Alert severity="info" sx={{
      mb: 3
    }}>A comparação usa contratos vigentes e encerrados de {year - 1}, na mesma série, turma e turno. Rescindidos não comprovam, por si só, transferência escolar. Turmas apenas do histórico também são exibidas.</Alert>
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr)',
      gap: 3
    }}>
      <Paper sx={panel}>
        <Typography variant="h6" sx={{
          fontWeight: 700
        }}>Todas as turmas ({stats.classrooms.length})</Typography>
        <Typography variant="body2" color="text.secondary" sx={{
          mt: .5,
          mb: 2
        }}>{stats.classrooms.filter(row => row.total > 0).length} turmas com matrículas na consulta atual. Abra a série para conferir os detalhes; os totais ficam visíveis com o grupo fechado.</Typography>
        {[...new Set(classrooms.map(row => row.grade))].sort(gradeSort).map(grade => {
          const rows = classrooms.filter(row => row.grade === grade);
          const total = rows.reduce((sum, row) => sum + row.total, 0);
          return <Accordion key={grade} disableGutters sx={{
            boxShadow: 'none',
            border: '1px solid #e1e9f0',
            '&:before': {
              display: 'none'
            }
          }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}><Box sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 1
              }}><Typography sx={{
                  fontWeight: 700
                }}>{grade}</Typography><Chip size="small" label={`${rows.length} turmas`} /><Chip size="small" color="primary" variant="outlined" label={`${total} matrículas em ${year}`} /></Box></AccordionSummary>
            <AccordionDetails sx={{
              p: {
                xs: 1,
                md: 2
              }
            }}><StatisticsTable title={`Turmas de ${grade}`} description="Inclui turmas apenas do histórico ou com contratos rescindidos. Clique no cabeçalho para ordenar." columns={columns} rows={rows} sort={sort} onSort={key => setSort(old => ({
                key,
                dir: old.key === key && old.dir === 'asc' ? 'desc' : 'asc'
              }))} /></AccordionDetails>
          </Accordion>;
        })}
      </Paper>
      <EnrollmentHistory year={year} unit={unit} />
      <StatisticsTable title="Consolidação por série" description="Comparação da mesma série em anos diferentes; não representa a progressão dos mesmos alunos." columns={columns.filter(column => column.key !== 'shift').map(column => column.key === 'name' ? {
        ...column,
        label: 'Série'
      } : column)} rows={stats.grades} />
      {previous !== null && <EnrollmentCohortReport cohorts={stats.cohorts} year={year} />}
    </Box>
    <Box sx={{
      mt: 2
    }}><Chip size="small" variant="outlined" label="Integral excluído em todas as bases" /></Box>
  </Box>;
};
